import React from 'react';
import { useTranslation } from 'react-i18next';

import { formatAmountWithCode } from '../../../../data/billing';
import { FINISH } from '../../finish.tokens';
import {
  Root,
  Scroll,
  SectionLabel,
  SectionLabelLg,
  EmailInput,
  Methods,
  ErrorAlert,
  ErrorAlertIcon,
  ErrorAlertText,
  Footer,
  TotalRow,
  TotalLabels,
  TotalLabel,
  TotalSub,
  TotalAmount,
  PayButton,
  PayButtonLabel,
} from './PaymentChrome.styles';

/** Warning glyph (rounded triangle + exclamation) — stroke from the error token. */
const WarningGlyph = (): React.ReactElement => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 3.6 21.2 19.5a1.4 1.4 0 0 1-1.2 2.1H4a1.4 1.4 0 0 1-1.2-2.1L12 3.6Z"
      stroke={FINISH.errorIconStroke}
      strokeWidth="1.6"
      strokeLinejoin="round"
    />
    <path d="M12 9.5v4.2" stroke={FINISH.errorIconStroke} strokeWidth="1.8" strokeLinecap="round" />
    <circle cx="12" cy="17.2" r="1.05" fill={FINISH.errorIconStroke} />
  </svg>
);

export type PaymentChromeProps = {
  email: string;
  onEmail: (value: string) => void;
  totalLabel: string;
  totalSub?: string;
  amount: number;
  currencySymbol: string;
  /** ISO currency code (e.g. "USD"/"MXN") shown trailing the amount to disambiguate the $ glyph. */
  currencyCode: string;
  payLabel: string;
  paying: boolean;
  error?: string | null;
  onPay: () => void;
  /** The payment-method region (Stripe Elements, or demo placeholders). */
  children: React.ReactNode;
};

/**
 * Presentational shell shared by the live (Stripe) and demo payment forms. A
 * flex column: scrollable "Contact info" + payment-method region on top, the
 * order total + Pay button pinned at the bottom.
 */
export const PaymentChrome = ({
  email,
  onEmail,
  totalLabel,
  totalSub,
  amount,
  currencySymbol,
  currencyCode,
  payLabel,
  paying,
  error,
  onPay,
  children,
}: PaymentChromeProps): React.ReactElement => {
  const { t } = useTranslation();

  return (
    <Root>
      <Scroll>
        <SectionLabel text="Contact info" tag="paragraph" />
        <EmailInput
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          placeholder={t('you@email.com')}
          aria-label={t('Email address')}
        />

        <SectionLabelLg text="Choose payment method" tag="paragraph" />
        <Methods>{children}</Methods>

        {error ? (
          <ErrorAlert role="alert">
            <ErrorAlertIcon>
              <WarningGlyph />
            </ErrorAlertIcon>
            <ErrorAlertText text={error} tag="paragraph" />
          </ErrorAlert>
        ) : null}
      </Scroll>

      <Footer>
        <TotalRow>
          <TotalLabels>
            <TotalLabel text={totalLabel} tag="span" />
            {totalSub ? <TotalSub text={totalSub} tag="span" /> : null}
          </TotalLabels>
          <TotalAmount
            text="{{symbol}}{{amount}}"
            tag="span"
            placeholders={{ symbol: currencySymbol, amount: formatAmountWithCode(amount, currencyCode) }}
          />
        </TotalRow>
        <PayButton type="button" data-ga-id="cfg-finish-payment-pay" onClick={onPay} disabled={paying}>
          <PayButtonLabel text={paying ? t('Processing…') : payLabel} tag="span" />
        </PayButton>
      </Footer>
    </Root>
  );
};

export default PaymentChrome;
